import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react'
import { Info, MoreHorizontal } from 'lucide-react'
import { Explicatie } from './Explicatie'

/**
 * Tabloul cu coloane-stari (Kanban) — piesa comuna, nu pagina unica (Madalin, 12.10.2026: „o
 * singura piesa Tablou"). Azi o folosesc Task-urile; maine poate servi orice flux cu stari
 * (cereri de marfa, comenzi), deci arata si se foloseste la fel peste tot.
 *
 * Ce stie piesa: coloanele, tragerea dintr-o coloana in alta, meniul „Mută în…" (alternativa la
 * tragere, ceruta de WCAG 2.2 SC 2.5.7 — merge din tastatura si pe telefon, unde tragerea HTML nu
 * merge) si anuntul pentru cititoarele de ecran dupa fiecare mutare.
 *
 * Ce NU stie: regulile. Pagina spune, pentru fiecare element, in ce coloane se poate muta
 * (`tinte`); o coloana care nu e tinta nu se aprinde la tragere si nu apare in meniu. Regula
 * reala ramane in API — piesa doar nu ofera ce oricum ar fi refuzat.
 */

export interface ColoanaTablou<S extends string> {
  id: S
  nume: string
  /** Culoarea bulinei din cap — clasa de fundal (`bg-b4l-blue`). */
  bulina: string
  /** O propozitie: ce inseamna coloana. Apare la hover pe (i). */
  ajutor: string
  /** Ce scrie cand coloana e goala — de ce e goala, nu doar „nimic". */
  gol: string
}

interface ContextTragere<S extends string> {
  trase: { cheie: string; tinte: S[] } | null
  incepe: (cheie: string, tinte: S[]) => void
  termina: () => void
  lasa: (spre: S) => void
}

const Context = createContext<ContextTragere<string> | null>(null)

/** Anuntul pentru cititoarele de ecran: o regiune `aria-live`, golita si rescrisa la fiecare mutare. */
function useAnunt() {
  const [text, setText] = useState('')
  const anunta = (t: string) => {
    setText('')
    window.setTimeout(() => setText(t), 30)
  }
  return { text, anunta }
}

export function Tablou<T, S extends string>({
  coloane,
  elemente,
  cheie,
  coloana,
  tinte,
  laMutare,
  eticheta,
  randeaza,
  benzi = [],
  inCap,
  inTitlu,
  inainteDeCarduri,
  dedesubt,
}: {
  coloane: ColoanaTablou<S>[]
  elemente: T[]
  cheie: (e: T) => string | number
  coloana: (e: T) => S
  /** In ce coloane se poate muta elementul, acum, de catre omul care se uita. */
  tinte: (e: T) => S[]
  laMutare: (e: T, spre: S) => void
  /** Titlul elementului, pentru anunt si pentru meniul „Mută în". */
  eticheta: (e: T) => string
  /** Fata cardului. Piesa adauga in jurul ei tragerea si meniul. */
  randeaza: (e: T) => ReactNode
  /** Ceva in plus sub capul coloanei (un avertisment), daca e cazul. */
  inCap?: (c: ColoanaTablou<S>, nr: number) => ReactNode
  /** In dreapta capului, langa (i) — de ex. butonul de ordine al coloanei. */
  inTitlu?: (c: ColoanaTablou<S>, nr: number) => ReactNode
  /** Deasupra cardurilor (adaugarea rapida). */
  inainteDeCarduri?: (c: ColoanaTablou<S>) => ReactNode
  /**
   * Tinte care nu sunt coloane: benzile de sub tablou (ca „Gata"). Apar in meniul „Mută în";
   * zona lor o deseneaza pagina, in `dedesubt`, cu `ZonaTablou`.
   */
  benzi?: ColoanaTablou<S>[]
  /** Sub coloane — de ex. banda „Gata", care e si ea tinta de tragere (`ZonaTablou`). */
  dedesubt?: ReactNode
}) {
  const [trase, setTrase] = useState<{ cheie: string; tinte: S[] } | null>(null)
  const { text, anunta } = useAnunt()
  const dupaCheie = new Map(elemente.map((e) => [String(cheie(e)), e]))
  const toate = [...coloane, ...benzi]
  const numeColoana = (id: S) => toate.find((c) => c.id === id)?.nume ?? id

  const muta = (e: T, spre: S) => {
    if (coloana(e) === spre || !tinte(e).includes(spre)) return
    anunta(`„${eticheta(e)}” mutat din ${numeColoana(coloana(e))} în ${numeColoana(spre)}.`)
    laMutare(e, spre)
  }

  const ctx: ContextTragere<S> = {
    trase,
    incepe: (k, t) => setTrase({ cheie: k, tinte: t }),
    termina: () => setTrase(null),
    lasa: (spre) => {
      const e = trase ? dupaCheie.get(trase.cheie) : undefined
      setTrase(null)
      if (e) muta(e, spre)
    },
  }

  return (
    <Context.Provider value={ctx as unknown as ContextTragere<string>}>
      <div className="grid gap-3">
        {/*
          Coloanele se intind cat cea mai lunga (fara `items-start`): de la cardul de JOS al unei
          coloane lungi, „Finalizate” e tot pe ecran, langa el (27.10.2026).
        */}
        <div
          className="grid auto-cols-[minmax(240px,1fr)] grid-flow-col gap-2.5 overflow-x-auto pb-2"
          role="list"
          aria-label="Coloane"
        >
          {coloane.map((c) => {
            const din = elemente.filter((e) => coloana(e) === c.id)
            return (
              <ZonaTablou key={c.id} id={c.id} eticheta={c.nume} fel="coloana">
                <div className="grid gap-0.5 px-1.5 pb-0.5 pt-1">
                  <div className="flex items-center justify-between gap-1.5">
                    <h3 className="flex items-center gap-1.5 text-corp font-semibold text-b4l-text">
                      <span
                        className={`h-2 w-2 shrink-0 rounded-full ${c.bulina}`}
                        aria-hidden="true"
                      />
                      {c.nume}
                      <span className="rounded-full border border-b4l-border bg-b4l-surface px-1.5 text-eticheta font-semibold tabular-nums text-b4l-text-muted">
                        {din.length}
                      </span>
                    </h3>
                    <span className="flex items-center gap-1">
                      {inTitlu?.(c, din.length)}
                      <Explicatie text={c.ajutor} subliniat={false}>
                        <Info
                          size={14}
                          className="text-b4l-text-dim"
                          aria-label={`Despre „${c.nume}”`}
                        />
                      </Explicatie>
                    </span>
                  </div>
                  {inCap?.(c, din.length)}
                </div>
                {inainteDeCarduri?.(c)}
                {din.length === 0 ? (
                  <p className="px-1.5 py-5 text-center text-mic text-b4l-text-dim">{c.gol}</p>
                ) : (
                  din.map((e) => (
                    <CardTablou
                      key={cheie(e)}
                      cheie={String(cheie(e))}
                      tinte={tinte(e).filter((t) => t !== coloana(e))}
                      coloane={toate}
                      eticheta={eticheta(e)}
                      laMutare={(spre) => muta(e, spre)}
                    >
                      {randeaza(e)}
                    </CardTablou>
                  ))
                )}
              </ZonaTablou>
            )
          })}
        </div>
        {dedesubt}
      </div>
      <div className="sr-only" aria-live="polite">
        {text}
      </div>
    </Context.Provider>
  )
}

/**
 * O zona in care se poate lasa un card: o coloana, sau o banda separata. Se aprinde doar cand
 * cardul tras chiar se poate muta acolo.
 */
export function ZonaTablou({
  id,
  eticheta,
  fel = 'banda',
  className = '',
  children,
}: {
  id: string
  eticheta: string
  fel?: 'coloana' | 'banda'
  className?: string
  children: ReactNode
}) {
  const ctx = useContext(Context)
  const [peste, setPeste] = useState(false)
  const permisa = !!ctx?.trase?.tinte.includes(id)
  const baza =
    fel === 'coloana'
      ? 'grid min-h-[320px] content-start gap-2 rounded-caseta border bg-b4l-bg/70 p-2'
      : 'grid gap-2 rounded-caseta border p-2.5'
  const aprins =
    permisa && peste
      ? 'border-b4l-blue bg-b4l-blue-light'
      : permisa
        ? 'border-dashed border-b4l-blue'
        : 'border-b4l-border'
  return (
    <section
      role="listitem"
      aria-label={eticheta}
      className={`${baza} ${aprins} transition-colors ${className}`}
      onDragOver={(e) => {
        if (!permisa) return
        e.preventDefault()
        e.dataTransfer.dropEffect = 'move'
        if (!peste) setPeste(true)
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setPeste(false)
      }}
      onDrop={(e) => {
        e.preventDefault()
        setPeste(false)
        if (permisa) ctx?.lasa(id)
      }}
    >
      {children}
    </section>
  )
}

/**
 * Invelisul cardului: tras cu mouse-ul, mutat din meniul ⋯ („Mută în…"). Fata o da pagina.
 * Un card fara nicio tinta (al altcuiva) nu se trage si n-are meniu.
 */
function CardTablou<S extends string>({
  cheie,
  tinte,
  coloane,
  eticheta,
  laMutare,
  children,
}: {
  cheie: string
  tinte: S[]
  coloane: ColoanaTablou<S>[]
  eticheta: string
  laMutare: (spre: S) => void
  children: ReactNode
}) {
  const ctx = useContext(Context)
  const [meniu, setMeniu] = useState(false)
  const radacina = useRef<HTMLDivElement>(null)
  const butonMeniu = useRef<HTMLButtonElement>(null)
  const mobil = tinte.length > 0
  const tras = ctx?.trase?.cheie === cheie

  // Meniul se inchide la un clic in afara lui.
  useEffect(() => {
    if (!meniu) return
    const inchide = (e: MouseEvent) => {
      if (!radacina.current?.contains(e.target as Node)) setMeniu(false)
    }
    document.addEventListener('mousedown', inchide)
    return () => document.removeEventListener('mousedown', inchide)
  }, [meniu])

  // Cu meniul deschis, sagetile trec de la o optiune la alta; Escape il inchide si intoarce focusul.
  const tasteMeniu = (e: KeyboardEvent<HTMLDivElement>) => {
    const optiuni = Array.from(
      e.currentTarget.querySelectorAll<HTMLButtonElement>('[role="menuitem"]'),
    )
    const i = optiuni.indexOf(document.activeElement as HTMLButtonElement)
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      optiuni[(i + 1) % optiuni.length]?.focus()
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      optiuni[(i - 1 + optiuni.length) % optiuni.length]?.focus()
    } else if (e.key === 'Escape') {
      e.stopPropagation()
      setMeniu(false)
      butonMeniu.current?.focus()
    }
  }

  return (
    <div
      ref={radacina}
      className={`group relative ${tras ? 'opacity-40' : ''}`}
      draggable={mobil}
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move'
        e.dataTransfer.setData('text/plain', cheie)
        ctx?.incepe(cheie, tinte)
      }}
      onDragEnd={() => ctx?.termina()}
    >
      {children}
      {mobil && (
        <div className="absolute right-1.5 top-1.5">
          <button
            ref={butonMeniu}
            type="button"
            aria-haspopup="menu"
            aria-expanded={meniu}
            aria-label={`Mută „${eticheta}” în altă coloană`}
            onClick={(e) => {
              e.stopPropagation()
              setMeniu((m) => !m)
            }}
            className={`rounded-md border border-b4l-border bg-b4l-surface-ridicata p-0.5 text-b4l-text-muted hover:text-b4l-text focus-visible:opacity-100 ${
              meniu
                ? 'opacity-100'
                : 'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:opacity-100'
            }`}
          >
            <MoreHorizontal size={15} />
          </button>
          {meniu && (
            <div
              role="menu"
              aria-label="Mută în"
              onKeyDown={tasteMeniu}
              className="absolute right-0 top-7 z-20 grid min-w-[160px] rounded-caseta border border-b4l-border bg-b4l-surface-ridicata p-1 shadow-ridicat"
            >
              <span className="px-2 pb-1 pt-0.5 text-eticheta font-semibold uppercase tracking-wide text-b4l-text-dim">
                Mută în
              </span>
              {coloane
                .filter((c) => tinte.includes(c.id))
                .map((c, i) => (
                  <button
                    key={c.id}
                    type="button"
                    role="menuitem"
                    autoFocus={i === 0}
                    onClick={(e) => {
                      e.stopPropagation()
                      setMeniu(false)
                      laMutare(c.id)
                    }}
                    className="flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-corp text-b4l-text hover:bg-b4l-bg focus-visible:bg-b4l-bg"
                  >
                    <span className={`h-2 w-2 rounded-full ${c.bulina}`} aria-hidden="true" />
                    {c.nume}
                  </button>
                ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
